// src/products/analysis/pages/FindCompanyPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Save, Check, Loader2, AlertCircle, Newspaper } from 'lucide-react';
import { useGlobalStore } from '../../../state/globalStore';
import { getStockData, searchCompanies } from '../services/finnhubService';
import { saveStockToPortfolio, isStockSaved } from '../services/portfolioService';

// Helper to format large numbers for display
const formatLargeNumber = (num) => {
  if (!num) return '';
  if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toLocaleString();
};

const FindCompanyPage = () => {
  const { user } = useGlobalStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Search for companies
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setShowResults(true);

    try {
      const results = await searchCompanies(searchQuery);
      setSearchResults(results.slice(0, 10)); // Limit to 10 results
    } catch (err) {
      setError('Failed to search companies. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Select a company and fetch its data
  const handleSelectCompany = async (result) => {
    setLoading(true);
    setError(null);
    setSelectedStock(null);
    setIsSaved(false);

    try {
      // Use displaySymbol if available (includes exchange suffix), otherwise use symbol
      const symbol = result.displaySymbol || result.symbol;
      console.log('Fetching data for symbol:', symbol, 'Full result:', result);
      
      const stockData = await getStockData(symbol);
      
      // Check if we got at least basic data
      if (!stockData || (!stockData.currentPrice && stockData.currentPrice !== 0)) {
        throw new Error(`Unable to fetch price data for ${symbol}. This may be an unsupported exchange or the symbol format may be incorrect.`);
      }
      
      setSelectedStock(stockData);
      setShowResults(false);

      // Check if already saved (use default user ID for localStorage)
      const userId = user?.id || 'default_user';
      const saved = await isStockSaved(userId, symbol);
      setIsSaved(saved);
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch stock data. Please try again.';
      setError(errorMsg);
      console.error('Error fetching stock data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save stock to portfolio
  const handleSaveStock = async () => {
    if (!selectedStock) {
      setError('No stock selected.');
      return;
    }

    if (isSaved) {
      setError('This stock is already in your portfolio.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Use any user identifier, or just use a default since we're using localStorage
      const userId = user?.id || 'default_user';
      await saveStockToPortfolio(userId, selectedStock);
      setIsSaved(true);
      setError(null); // Clear any previous errors
      // Show success feedback
      setTimeout(() => {
        // Optionally clear success message after 3 seconds
      }, 3000);
    } catch (err) {
      setError(`Failed to save stock: ${err.message || 'Please try again.'}`);
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Find Company</h1>
        <p className="mt-1 text-sm text-gray-500">
          Search for companies by ticker symbol or name to view financial data
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter ticker symbol (e.g., AAPL, TSLA) or company name"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !searchQuery.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            Search
          </button>
        </form>

        {/* Error/Success Message */}
        {error && (
          <div className={`mt-4 p-3 border rounded-md flex items-center gap-2 ${
            error.includes('Failed') 
              ? 'bg-red-50 border-red-200 text-red-800' 
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}
        {isSaved && !error && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-green-800">
            <Check className="w-5 h-5" />
            <span className="text-sm">Stock saved to portfolio!</span>
          </div>
        )}

        {/* Search Results */}
        {showResults && searchResults.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-700">Search Results</p>
            </div>
            <div className="divide-y divide-gray-200">
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectCompany(result)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {result.displaySymbol || result.symbol || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">{result.description || result.name || 'N/A'}</p>
                    </div>
                    <div className="text-sm text-gray-400">
                      {result.type || 'Stock'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {showResults && searchResults.length === 0 && !loading && (
          <div className="mt-4 text-center py-8 text-gray-500">
            No companies found. Try a different search term.
          </div>
        )}
      </div>

      {/* Stock Data Display */}
      {selectedStock && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {selectedStock.logo && (
                <img
                  src={selectedStock.logo}
                  alt={selectedStock.name}
                  className="w-16 h-16 rounded-lg object-contain"
                />
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedStock.name}</h2>
                <p className="text-lg text-gray-600">
                  {selectedStock.symbol} · {selectedStock.exchange}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/market-data/chart/${selectedStock.symbol}`)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium flex items-center gap-2"
              >
                View Chart
              </button>
              <button
                onClick={() => navigate(`/market-data/news/${selectedStock.symbol}`)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium flex items-center gap-2"
              >
                <Newspaper className="w-4 h-4" />
                Get News
              </button>
              <button
                onClick={handleSaveStock}
                disabled={saving || isSaved}
                className={`px-4 py-2 rounded-md font-medium flex items-center gap-2 ${
                  isSaved
                    ? 'bg-green-100 text-green-800 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:bg-gray-400 disabled:cursor-not-allowed`}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : isSaved ? (
                  <>
                    <Check className="w-4 h-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save to Portfolio
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Financial Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Latest Share Price</p>
              <p className="text-2xl font-bold text-gray-900">{selectedStock.currentPrice}</p>
              <p className="text-xs text-gray-400 mt-1">{selectedStock.currency || 'USD'}</p>
              {selectedStock.priceChangePercent && (
                <p className={`text-sm mt-1 ${selectedStock.priceChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedStock.priceChangePercent}
                </p>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Market Cap</p>
              <p className="text-2xl font-bold text-gray-900">{selectedStock.marketCap}</p>
              <p className="text-xs text-gray-400 mt-1">
                {selectedStock.currency || 'USD'} · 
                {selectedStock._marketCap ? ` ${formatLargeNumber(selectedStock._marketCap)}` : ' Calculated'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Enterprise Value</p>
              <p className="text-2xl font-bold text-gray-900">{selectedStock.enterpriseValue}</p>
              <p className="text-xs text-gray-400 mt-1">
                {selectedStock.currency || 'USD'} · 
                {selectedStock._enterpriseValue ? ' From API' : ' N/A'}
                <br />
                <span className="text-xs">EV = Market Cap + Debt - Cash</span>
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500 mb-1">Shares Outstanding</p>
              <p className="text-2xl font-bold text-gray-900">{selectedStock.sharesOutstanding}</p>
              <p className="text-xs text-gray-400 mt-1">
                {selectedStock._sharesOutstanding ? 
                  `${selectedStock._sharesOutstanding.toLocaleString()} shares` : 
                  'N/A'}
              </p>
            </div>
          </div>

          {/* Additional Price Info */}
          {(selectedStock.high || selectedStock.low || selectedStock.open) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Price Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {selectedStock.open && (
                  <div>
                    <p className="text-xs text-gray-500">Open</p>
                    <p className="text-sm font-medium text-gray-900">{selectedStock.open.toFixed(2)}</p>
                  </div>
                )}
                {selectedStock.high && (
                  <div>
                    <p className="text-xs text-gray-500">High</p>
                    <p className="text-sm font-medium text-gray-900">{selectedStock.high.toFixed(2)}</p>
                  </div>
                )}
                {selectedStock.low && (
                  <div>
                    <p className="text-xs text-gray-500">Low</p>
                    <p className="text-sm font-medium text-gray-900">{selectedStock.low.toFixed(2)}</p>
                  </div>
                )}
                {selectedStock.previousClose && (
                  <div>
                    <p className="text-xs text-gray-500">Previous Close</p>
                    <p className="text-sm font-medium text-gray-900">{selectedStock.previousClose.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Company Website Link */}
          {selectedStock.weburl && (
            <div className="mt-6">
              <a
                href={selectedStock.weburl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Visit Company Website →
              </a>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && !selectedStock && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-gray-600">Loading stock data...</p>
        </div>
      )}
    </div>
  );
};

export default FindCompanyPage;

